import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Download,
  RefreshCw,
  Calendar,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  DollarSign,
  Search,
  ArrowLeft,
  Repeat,
} from "lucide-react";
import * as XLSX from "xlsx";

// Period shortcuts
const PERIOD_SHORTCUTS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "custom", label: "Custom Range" },
];

// Helper to format date as YYYY-MM-DD in local timezone
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper to calculate date range from shortcut
const getDateRange = (shortcut) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  switch (shortcut) {
    case "today":
      return {
        from: formatLocalDate(today),
        to: formatLocalDate(today)
      };
    case "yesterday": {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return {
        from: formatLocalDate(yesterday),
        to: formatLocalDate(yesterday)
      };
    }
    case "this_week": {
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      return {
        from: formatLocalDate(startOfWeek),
        to: formatLocalDate(today)
      };
    }
    case "last_week": {
      const startOfLastWeek = new Date(today);
      startOfLastWeek.setDate(today.getDate() - today.getDay() - 7);
      const endOfLastWeek = new Date(startOfLastWeek);
      endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
      return {
        from: formatLocalDate(startOfLastWeek),
        to: formatLocalDate(endOfLastWeek)
      };
    }
    case "this_month": {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return {
        from: formatLocalDate(startOfMonth),
        to: formatLocalDate(today)
      };
    }
    case "last_month": {
      const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      return {
        from: formatLocalDate(startOfLastMonth),
        to: formatLocalDate(endOfLastMonth)
      };
    }
    default:
      return { from: formatLocalDate(today), to: formatLocalDate(today) };
  }
};

export const SubscriptionPaymentsReport = ({ onBack }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total_count: 0,
    total_pages: 1
  });
  const [totals, setTotals] = useState({ total_amount: 0, total_payments: 0 });
  
  // Filters
  const [filters, setFilters] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("this_month");
  const [dateFrom, setDateFrom] = useState(() => getDateRange("this_month").from);
  const [dateTo, setDateTo] = useState(() => getDateRange("this_month").to);
  const [cityFilter, setCityFilter] = useState("all");
  const [riderSearch, setRiderSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Fetch filter options
  useEffect(() => {
    fetchFilters();
  }, []);

  // Fetch data when filters change
  useEffect(() => {
    fetchPayments();
  }, [dateFrom, dateTo, cityFilter, riderSearch, pagination.page]);

  const fetchFilters = async () => {
    try {
      const res = await axios.get(`${API}/reports/subscription-payments/filters?admin_id=${user?.id}`);
      setFilters(res.data);
    } catch (err) {
      console.error("Failed to fetch filters:", err);
    }
  };

  const fetchPayments = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("admin_id", user.id);
      params.append("page", pagination.page.toString());
      params.append("limit", "50");
      if (dateFrom) params.append("start_date", dateFrom);
      if (dateTo) params.append("end_date", dateTo);
      if (cityFilter !== "all") params.append("city_id", cityFilter);
      if (riderSearch) params.append("rider_search", riderSearch);
      
      const res = await axios.get(`${API}/reports/subscription-payments?${params}`);
      setPayments(res.data.rows || []);
      setPagination(res.data.pagination || pagination);
      setTotals(res.data.totals || { total_amount: 0, total_payments: 0 });
    } catch (err) {
      toast.error("Failed to load subscription payments");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    if (period !== "custom") {
      const range = getDateRange(period);
      setDateFrom(range.from);
      setDateTo(range.to);
      setPagination(prev => ({ ...prev, page: 1 }));
    }
  };

  const handleSearch = () => {
    setRiderSearch(searchInput);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const exportToExcel = () => {
    if (!payments.length) {
      toast.error("No data to export");
      return;
    }
    
    const exportData = payments.map(p => ({
      "City": p.city,
      "Rider Name": p.rider_name,
      "Phone": p.phone,
      "Plan": p.plan,
      "Amount (₹)": p.amount,
      "Payment Method": p.payment_method,
      "Date & Time": formatDateTime(p.recorded_at),
      "Reference": p.reference_number || "-",
      "Renewal": p.is_renewal ? "Yes" : "No"
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Subscription Payments");
    XLSX.writeFile(wb, `Subscription_Payments_${dateFrom}_to_${dateTo}.xlsx`);
    toast.success("Report exported successfully");
  };

  return (
    <div className="space-y-6" data-testid="subscription-payments-report">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-primary" />
              Subscription Payments Report
            </h2>
            <p className="text-muted-foreground text-sm mt-1">
              View all subscription payments received from riders
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchPayments} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={exportToExcel} data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Payments</p>
                <p className="text-3xl font-bold">{totals.total_payments}</p>
              </div>
              <CreditCard className="w-10 h-10 text-primary opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Amount</p>
                <p className="text-3xl font-bold text-green-600">₹{totals.total_amount.toLocaleString()}</p>
              </div>
              <DollarSign className="w-10 h-10 text-green-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Period Shortcuts */}
            <div>
              <label className="text-sm font-medium mb-1 block">Period</label>
              <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
                <SelectTrigger data-testid="period-select">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_SHORTCUTS.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date From */}
            <div>
              <label className="text-sm font-medium mb-1 block">From</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setSelectedPeriod("custom");
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                data-testid="date-from"
              />
            </div>

            {/* Date To */}
            <div>
              <label className="text-sm font-medium mb-1 block">To</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setSelectedPeriod("custom");
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                data-testid="date-to"
              />
            </div>

            {/* City Filter */}
            <div>
              <label className="text-sm font-medium mb-1 block">City</label>
              <Select 
                value={cityFilter} 
                onValueChange={(v) => {
                  setCityFilter(v);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <SelectTrigger data-testid="city-filter">
                  <SelectValue placeholder="All Cities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {filters?.cities?.map(city => (
                    <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Rider Search */}
            <div>
              <label className="text-sm font-medium mb-1 block">Rider</label>
              <div className="flex gap-2">
                <Input
                  placeholder="Name or phone..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  data-testid="rider-search"
                />
                <Button variant="outline" size="icon" onClick={handleSearch}>
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>City</TableHead>
                  <TableHead>Rider</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Payment Method</TableHead>
                  <TableHead>Date & Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto text-primary" />
                      <p className="text-muted-foreground mt-2">Loading...</p>
                    </TableCell>
                  </TableRow>
                ) : payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No subscription payments found for the selected filters
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((payment) => (
                    <TableRow key={payment.id} data-testid={`payment-row-${payment.id}`}>
                      <TableCell className="font-medium">{payment.city}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {payment.rider_name}
                          {payment.is_renewal && (
                            <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded" title="Renewal">
                              <Repeat className="w-3 h-3" />
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{payment.phone}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          payment.subscription_type === "fixed_daily" 
                            ? "bg-purple-100 text-purple-700"
                            : payment.subscription_type === "fixed_weekly"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-green-100 text-green-700"
                        }`}>
                          {payment.plan}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-green-600">
                        ₹{payment.amount}
                      </TableCell>
                      <TableCell>{payment.payment_method}</TableCell>
                      <TableCell>{formatDateTime(payment.recorded_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to{" "}
                {Math.min(pagination.page * pagination.limit, pagination.total_count)} of{" "}
                {pagination.total_count} payments
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  data-testid="prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm">
                  Page {pagination.page} of {pagination.total_pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.total_pages}
                  data-testid="next-page"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
